import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState';
import { WIKI, HOME } from '@confluence/named-routes/entry-points/routes';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';
import {
	GO_HOME_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper';
import { i18n } from '../globalNavigationTranslations';
import { ForYouIconComponent } from '../GlobalNavigationIcons';

import type { GlobalItemProps } from './globalItemProps';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks'))
			.fireHomeClickedAnalytics,
);

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};

export const ForYouItem: FC<GlobalItemProps> = () => {
	//takes globalItemProps but is not customizable so doesn't use them
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(isOnHomeSelector);

	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();

	return (
		<KeyboardShortcutWrapper
			href={homeUrl}
			deprecatedKeyboardShortcut={!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined}
			keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
			isAppNavigationShortcut
			onShortcutTriggered={fireClickAnalytics}
		>
			<HomeOnboardingTakeoverV2HomeNudge>
				<LinkMenuItem
					href={homeUrl}
					elemBefore={ForYouIconComponent}
					isSelected={isSelected}
					onClick={fireClickAnalytics}
				>
					<FormattedMessage {...i18n.forYou} />
				</LinkMenuItem>
			</HomeOnboardingTakeoverV2HomeNudge>
		</KeyboardShortcutWrapper>
	);
};
